import { useSearchParams } from 'react-router-dom';
import type { ChecklistStepProgress } from './onboardingApi.js';
import { branchProfilePath } from '../branch-settings/branchSettingsState.js';

export const BRANCH_SETUP_PARAM = 'setup';

const BRANCH_SCOPED_STEP_CODES = new Set([
  'opening-hours',
  'service-area',
  'fulfilment-settings',
  'go-live',
]);

const SETUP_FLOW_STEP_CODES = new Set([
  'create-branch',
  'opening-hours',
  'service-area',
  'fulfilment-settings',
  'catalogue',
  'go-live',
]);

const SETUP_FLOW_PATHS = new Set([
  '/create-branch',
  '/branch-profile',
  '/opening-hours',
  '/service-area',
  '/fulfilment-settings',
  '/product-list',
  '/product-editor',
  '/bulk-import',
  '/branch-status',
]);

/** Only these setup=1 routes may bypass the pre-live operational-section gate. */
export function isOnboardingSetupPath(pathname: string): boolean {
  return SETUP_FLOW_PATHS.has(pathname);
}

/** True when the merchant is in the post-terms WF-05 branch setup spine. */
export function useBranchSetupMode(): boolean {
  const [params] = useSearchParams();
  return params.get(BRANCH_SETUP_PARAM) === '1';
}

export function branchSetupPath(basePath: string, branchId?: string | null): string {
  const [pathname, search = ''] = basePath.split('?');
  const params = new URLSearchParams(search);
  params.set(BRANCH_SETUP_PARAM, '1');
  if (branchId) params.set('branchId', branchId);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function createBranchSetupPath(): string {
  return branchSetupPath('/create-branch');
}

export function openingHoursSetupPath(branchId: string): string {
  return branchSetupPath('/opening-hours', branchId);
}

export function serviceAreaSetupPath(branchId: string): string {
  return branchSetupPath('/service-area', branchId);
}

export function fulfilmentSetupPath(branchId: string): string {
  return branchSetupPath('/fulfilment-settings', branchId);
}

/** Collection-only branches do not need a delivery area. */
export function openingHoursNextSetupPath(branchId: string, supportsDelivery: boolean): string {
  return supportsDelivery ? serviceAreaSetupPath(branchId) : catalogueSetupPath(branchId);
}

export function fulfilmentPreviousSetupPath(): string {
  return createBranchSetupPath();
}

export function catalogueSetupPath(branchId?: string | null): string {
  return branchSetupPath('/product-list', branchId);
}

export function bulkImportSetupPath(branchId?: string | null): string {
  return branchSetupPath('/bulk-import', branchId);
}

export function productEditorSetupPath(productId?: string, branchId?: string | null): string {
  const base = productId
    ? `/product-editor?productId=${encodeURIComponent(productId)}`
    : '/product-editor?new=1';
  return branchSetupPath(base, branchId);
}

export function branchStatusSetupPath(branchId: string): string {
  return branchSetupPath('/branch-status', branchId);
}

/** Map MOD-007 activation blockers to setup-spine fix routes. */
export function activationBlockerFixRoute(
  requirement: string | undefined,
  branchId: string,
): string | null {
  switch (requirement) {
    case 'OPENING_HOURS':
      return openingHoursSetupPath(branchId);
    case 'SERVICE_AREA':
      return serviceAreaSetupPath(branchId);
    case 'FULFILMENT_METHOD':
      return fulfilmentSetupPath(branchId);
    case 'PICKUP_PHONE_E164':
    case 'BRANCH_COORDINATES':
    case 'PICKUP_LOCATION_CONFIRMED':
      return branchSetupPath(branchProfilePath(branchId));
    case 'PUBLISHED_PRODUCT':
      return catalogueSetupPath(branchId);
    case 'VERIFICATION_FULL':
      return '/beat-pay-setup';
    default:
      return null;
  }
}

/** Checklist and deep links include branchId + setup for branch-scoped onboarding steps. */
export function enrichChecklistStepRoutes(
  steps: ChecklistStepProgress[],
  branchId?: string | null,
): ChecklistStepProgress[] {
  return steps.map((step) => {
    if (!SETUP_FLOW_STEP_CODES.has(step.code)) {
      return step;
    }

    if (step.code === 'go-live') {
      return {
        ...step,
        route: branchId ? branchStatusSetupPath(branchId) : branchSetupPath('/branch-status'),
      };
    }

    if (step.code === 'catalogue') {
      return { ...step, route: catalogueSetupPath(branchId) };
    }

    const needsBranch = BRANCH_SCOPED_STEP_CODES.has(step.code);
    return {
      ...step,
      route: branchSetupPath(step.route, needsBranch ? branchId : undefined),
    };
  });
}

export function defaultSetupOpeningHoursDraft(): Record<
  string,
  { open: string; close: string; closed: boolean }
> {
  return {
    MONDAY: { open: '09:00', close: '22:00', closed: false },
    TUESDAY: { open: '09:00', close: '22:00', closed: false },
    WEDNESDAY: { open: '09:00', close: '22:00', closed: false },
    THURSDAY: { open: '09:00', close: '22:00', closed: false },
    FRIDAY: { open: '09:00', close: '22:00', closed: false },
    SATURDAY: { open: '09:00', close: '22:00', closed: false },
    SUNDAY: { open: '10:00', close: '21:00', closed: false },
  };
}
